// Issue #111 — animated count-up hook.
//
// Returns a Solid signal-getter that eases from the previously-rendered
// value to the latest input value. The transition is short (default
// 600ms) and uses an ease-out cubic curve so big jumps feel weighty
// without lagging the UI. Falls back to instant updates on the first
// render and whenever the user has reduced-motion enabled.

import { createSignal, createEffect, onCleanup } from 'solid-js';

const PREFERS_REDUCED = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useCountUp(input, opts = {}) {
  const duration = Math.max(50, opts.duration || 600);
  const [out, setOut] = createSignal(typeof input === 'function' ? (input() || 0) : (input || 0));
  let raf = 0;
  let from = out();
  let startedAt = 0;
  let target = from;

  createEffect(() => {
    const next = Number(typeof input === 'function' ? input() : input) || 0;
    if (next === target) return;
    target = next;
    if (PREFERS_REDUCED) {
      setOut(next);
      from = next;
      return;
    }
    from = out();
    startedAt = performance.now();
    if (raf) cancelAnimationFrame(raf);
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      // ease-out cubic
      const k = 1 - Math.pow(1 - t, 3);
      const v = from + (target - from) * k;
      setOut(t === 1 ? target : v);
      if (t < 1) raf = requestAnimationFrame(tick);
      else raf = 0;
    };
    raf = requestAnimationFrame(tick);
  });

  onCleanup(() => { if (raf) cancelAnimationFrame(raf); });

  return out;
}

export default useCountUp;
